import { useQueryClient } from '@tanstack/react-query';
// mantine
import { UnstyledButton } from '@mantine/core';
// icons
import { Edit, Trash2, Plus } from 'lucide-react';
// components
import { PopoverButton } from '@newo/ui-kit';
import { TAgentWithFlows } from '@newo/api/gateway';
import DateTime from '@/modules/DateTime/components/DateTime';
import AddFlowForm from '@/pages/Agents/components/AddFlowForm';
import LinkedPersona from '@/pages/Agents/components/LinkedPersona';
// utils
import { openConfirmDeleteModal } from '@/utils/ui.tsx';
import { useDeleteAgentMutation } from '@/services/designer/agents/agents.queries.ts';
import { QUERY_KEYS as BFF_QUERY_KEYS } from '@/services/bff/bff.queries.ts';

interface AgentRowProps {
    agent: TAgentWithFlows;
    onAgentEdit: (agent?: TAgentWithFlows) => void;
}

const AgentRow = ({ agent, onAgentEdit }: AgentRowProps) => {
    const queryClient = useQueryClient();
    const { mutate: deleteAgent } = useDeleteAgentMutation();

    const invalidateBFFQuery = () => {
        queryClient.invalidateQueries({
            queryKey: BFF_QUERY_KEYS.getAgentsList()
        });
    };

    const handleAgentEdit = () => {
        onAgentEdit(agent);
    };

    const handleDelete = () => {
        openConfirmDeleteModal({
            title: `Delete ${agent.idn} agent?`,
            children: (
                <>
                    Are you sure you want to delete {agent.idn} agent?{' '}
                    <span className="font-semibold text-red-6">
                        All flows and actors connected to this agent will be
                        deleted too.
                    </span>
                </>
            ),
            confirmLabel: 'Delete Agent',
            onConfirm: () => {
                deleteAgent(
                    { agentId: agent.id },
                    {
                        onSuccess: invalidateBFFQuery
                    }
                );
            }
        });
    };

    return (
        <div className="grid gap-1.5 rounded-md bg-gray-2 px-2.5 py-3 text-sm grid-cols-[20%_25%_1fr_1fr]">
            <div>
                {agent.idn}
                <div className="text-xs text-gray-6">
                    Created:{' '}
                    <DateTime
                        format="yyyy-LL-dd HH:mm a"
                        value={agent.created_at}
                    />
                </div>
            </div>
            <div>
                <LinkedPersona persona={agent.persona} />
            </div>
            <div>{agent.description}</div>
            <div className="flex gap-4 self-center">
                <UnstyledButton
                    className="ml-auto"
                    title="Delete Agent"
                    onClick={handleDelete}
                >
                    <Trash2 />
                </UnstyledButton>
                <UnstyledButton onClick={handleAgentEdit}>
                    <Edit />
                </UnstyledButton>
                <PopoverButton title="Add Flow" trigger={<Plus />}>
                    {(close) => (
                        <AddFlowForm
                            agentId={agent.id}
                            onClose={close}
                            onSubmit={invalidateBFFQuery}
                        />
                    )}
                </PopoverButton>
            </div>
        </div>
    );
};

export default AgentRow;
